<template>
    <section class="payroll-history">
        <b-button v-if="summary.permissions && summary.permissions.can_resolve_history_people" size="sm" variant="outline-secondary" class="mb-3"
            :to="{path: '/timesheets/history-people', query: {month: String(summary.period.starts_on).slice(0, 7)}}">Сопоставить сотрудников архива</b-button>
        <b-alert show variant="info">
            Закрытые данные старой системы. Суммы и оценки сохранены из источников; перерасчёт и выплата здесь недоступны. Закрытие месяца не подтверждает выплату.
        </b-alert>
        <b-alert v-if="missingSalary.length" show variant="warning">
            Для {{ missingSalary.length }} строк табелей в источниках нет начисления. Это пропуск зарплатной истории, сумма не считается нулевой.
            <details><summary>Показать строки</summary>
                <ul><li v-for="row in missingSalary" :key="row.line_id">
                    {{ row.employee_name_snapshot }} ·
                    <router-link :to="timesheetLink(row.timesheet_id)">Исходный табель</router-link>
                </li></ul>
            </details>
        </b-alert>
        <div v-if="!runs.length" class="text-muted mb-3">В этом месяце исторических начислений нет.</div>
        <section v-for="run in runs" :key="run.id" class="payroll-history__section">
            <header><h2>{{ run.currency }} · исторические начисления</h2><strong>{{ money(run.totals.final_amount_minor, run.currency) }}</strong></header>
            <div class="table-responsive"><table class="table table-sm table-bordered mb-0">
                <thead><tr><th>Сотрудник в источнике</th><th>Принято часов</th><th>Принято дней</th><th>Расчёт</th><th>Изменение</th><th>Окончательное решение</th><th>Источники</th></tr></thead>
                <tbody><tr v-for="line in run.lines" :key="line.id">
                    <td>{{ line.employee_name_snapshot }}<small v-if="line.resolved_employee_id" class="d-block text-muted">Связан с карточкой №{{ line.resolved_employee_id }}</small></td>
                    <td class="text-right">{{ number(line.accepted_minutes / 60) }}</td>
                    <td class="text-right">{{ number(line.accepted_days) }}</td>
                    <td class="text-right">{{ money(line.calculated_amount_minor, run.currency) }}<small v-if="line.calculated_amount_minor === null" class="d-block text-muted">Нет расчётной суммы в источнике</small></td>
                    <td class="text-right text-nowrap">{{ money(line.manual_adjustment_minor, run.currency) }}</td>
                    <td class="text-right text-nowrap">{{ money(line.final_amount_minor, run.currency) }}</td>
                    <td><details><summary>Открыть источники</summary>
                        <div v-for="(source, index) in payrollSources(line)" :key="index" class="payroll-history__source">
                            <a v-if="sourceUrl(source)" :href="sourceUrl(source)" target="_blank" rel="noopener noreferrer">{{ sourceLabel(source) }}</a>
                            <span v-else>{{ sourceLabel(source) }}</span>
                            <dl v-if="sourceFields(source).length" class="mb-1">
                                <template v-for="field in sourceFields(source)"><dt :key="`${field.key}-label`">{{ field.key }}</dt><dd :key="`${field.key}-value`">{{ field.value }}</dd></template>
                            </dl>
                            <dl v-if="sourceNotes(source).length" class="mb-1">
                                <template v-for="field in sourceNotes(source)"><dt :key="`${field.key}-note-label`">Примечание {{ field.key }}</dt><dd :key="`${field.key}-note-value`"><q class="user-quote">{{ field.value }}</q></dd></template>
                            </dl>
                        </div>
                    </details></td>
                </tr></tbody>
            </table></div>
        </section>
        <section v-if="payrollInputs.length" class="payroll-history__section">
            <header><h2>Ведомость без решения о начислении</h2><span>{{ payrollInputs.length }} записей</span></header>
            <p class="small text-muted">Окончательная сумма не указана. Ставки, исходные данные и примечания сохранены; нулевое начисление не создавалось.</p>
            <div class="table-responsive"><table class="table table-sm table-bordered mb-0">
                <thead><tr><th>Сотрудник в источнике</th><th>Принято дней</th><th>Расчёт</th><th>Источник</th></tr></thead>
                <tbody><tr v-for="row in payrollInputs" :key="row.id">
                    <td>{{ row.employee_name_snapshot }}<small v-if="row.resolved_employee_id || row.employee_id" class="d-block text-muted">Связан с карточкой №{{ row.resolved_employee_id || row.employee_id }}</small></td>
                    <td>{{ number(row.accepted_days) }}</td><td>{{ money(inputCalculatedAmount(row), row.currency) }}</td>
                    <td><a v-if="sourceUrl(row)" :href="sourceUrl(row)" target="_blank" rel="noopener noreferrer">{{ sourceLabel(row) }}</a>
                        <details><summary>Исходные значения и примечания</summary><dl>
                            <template v-for="field in sourceFields(row)"><dt :key="`${field.key}-label`">{{ field.key }}</dt><dd :key="`${field.key}-value`">{{ field.value }}</dd></template>
                            <template v-for="field in sourceNotes(row)"><dt :key="`${field.key}-note-label`">Примечание {{ field.key }}</dt><dd :key="`${field.key}-note-value`"><q class="user-quote">{{ field.value }}</q></dd></template>
                        </dl></details>
                    </td>
                </tr></tbody>
            </table></div>
        </section>
        <section v-if="assessments.length" class="payroll-history__section">
            <header><h2>Оценки ИТР из источников</h2><span>{{ assessments.length }} записей</span></header>
            <div class="table-responsive"><table class="table table-sm table-bordered mb-0">
                <thead><tr><th>Сотрудник в источнике</th><th>Оценил</th><th>Значение</th><th>Комментарий</th><th>Источник</th></tr></thead>
                <tbody><tr v-for="row in assessments" :key="row.id">
                    <td>{{ row.employee_name_snapshot }}</td><td>{{ row.assessor_source && row.assessor_source.name || 'Не указан' }}</td>
                    <td>{{ assessmentValue(row.value) }}</td><td><q v-if="row.comment" class="user-quote">{{ row.comment }}</q><span v-else>—</span></td>
                    <td><div v-for="(source, index) in assessmentSources(row)" :key="index">
                        <a v-if="sourceUrl(source)" :href="sourceUrl(source)" target="_blank" rel="noopener noreferrer">{{ sourceLabel(source) }}</a><span v-else>{{ sourceLabel(source) }}</span>
                    </div></td>
                </tr></tbody>
            </table></div>
        </section>
        <details v-if="warnings.length" class="mb-3"><summary>Замечания к исходным данным ({{ warnings.length }})</summary>
            <ul><li v-for="(warning, index) in warnings" :key="index">{{ warning.message || warning.text || warning.code || warning }}</li></ul>
        </details>
        <section v-if="timesheets.length" class="payroll-history__section">
            <header><h2>Исходные табели месяца</h2></header>
            <ul><li v-for="sheet in timesheets" :key="sheet.id"><router-link :to="timesheetLink(sheet.id)">{{ archiveLabel(sheet) }}</router-link></li></ul>
        </section>
    </section>
</template>

<script>
import {archiveLabel} from '../Timesheets/timesheetArchiveLabel';
export default {
    name: 'PayrollHistory',
    props: {summary: {type: Object, required: true}},
    computed: {
        runs() { return this.summary.historical_runs || []; },
        payrollInputs() { return this.summary.historical_payroll_inputs || []; },
        assessments() { return this.summary.historical_assessments || []; },
        timesheets() { return this.summary.historical_timesheets || []; },
        reconciliation() { return this.summary.historical_reconciliation || {}; },
        missingSalary() { return this.reconciliation.missing_salary || []; },
        warnings() { return this.reconciliation.diagnostics && this.reconciliation.diagnostics.warnings || []; }
    },
    methods: {
        archiveLabel,
        number(value) { return value === null || value === undefined ? '—' : Number(value).toLocaleString('ru-RU', {maximumFractionDigits: 3}); },
        money(value, currency) { return value === null || value === undefined ? '—' : new Intl.NumberFormat('ru-RU', {style: 'currency', currency}).format(Number(value) / 100); },
        assessmentValue(value) { return value === null || value === undefined ? '—' : String(value); },
        timesheetLink(id) { return {path: `/timesheets/${id}`, query: {month: String(this.summary.period.starts_on).slice(0, 7), from: 'payroll'}}; },
        payrollSources(line) {
            const source = line.source_payload || {};
            return Array.isArray(source.rows) ? source.rows : Array.isArray(source.source_rows) ? source.source_rows : [source];
        },
        assessmentSources(row) {
            const source = row.source_payload || {};
            const metadata = source.source_payload || source;
            return Array.isArray(metadata.sources) && metadata.sources.length ? metadata.sources : [source];
        },
        sourceMetadata(source) {
            let metadata = source || {};
            // Applied payroll rows wrap the original record once; standalone input DTOs wrap it twice.
            for (let depth = 0; depth < 2 && metadata.source_payload && typeof metadata.source_payload === 'object'; depth++) {
                metadata = metadata.source_payload;
            }
            return metadata;
        },
        inputCalculatedAmount(row) {
            const metadata = this.sourceMetadata(row);
            return metadata.source_totals && metadata.source_totals.calculated_amount_minor !== undefined
                ? metadata.source_totals.calculated_amount_minor : row.calculated_amount_minor;
        },
        sourceUrl(source) {
            const metadata = this.sourceMetadata(source);
            const location = metadata.source || {};
            const row = Number(location.row);
            const range = Number.isSafeInteger(row) && row > 0 ? `&range=A${row}:AZ${row}` : '';
            const original = source && source.source_payload || {};
            const value = location.spreadsheet_id
                ? `https://docs.google.com/spreadsheets/d/${encodeURIComponent(location.spreadsheet_id)}/edit#gid=${encodeURIComponent(location.sheet_id || 0)}${range}`
                : source && (source.source_file_url || source.source_file && source.source_file.url
                    || original.source_file && original.source_file.url);
            return /^https:\/\/docs\.google\.com\//.test(value || '') ? value : null;
        },
        sourceLabel(source) {
            const row = source || {};
            const file = row.source_file || row.source_payload && row.source_payload.source_file || {};
            const metadata = this.sourceMetadata(row);
            const location = metadata.source || {};
            const name = row.source_sheet_name || file.sheet_name || location.sheet_name || file.name || row.source_file_name || 'Исходная таблица';
            const rowNumber = location.row || row.source_row;
            return rowNumber ? `${name} · строка ${rowNumber}` : name;
        },
        sourceFields(source) {
            const metadata = this.sourceMetadata(source);
            const fields = metadata.raw_source_cells || {};
            return Object.keys(fields).filter(key => fields[key] !== null && fields[key] !== undefined && fields[key] !== '').map(key => ({
                key: Array.isArray(fields) ? this.columnName(Number(key)) : key,
                value: String(fields[key])
            }));
        },
        sourceNotes(source) {
            const metadata = this.sourceMetadata(source);
            const notes = metadata.raw_source_notes || {};
            return Object.keys(notes).filter(key => notes[key] !== null && notes[key] !== undefined && notes[key] !== '').map(key => ({
                key: Array.isArray(notes) ? this.columnName(Number(key)) : key, value: String(notes[key])
            }));
        },
        columnName(index) {
            let result = '';
            for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26)) {
                result = String.fromCharCode(65 + (value - 1) % 26) + result;
            }
            return result;
        }
    }
};
</script>

<style scoped>
.payroll-history__section { margin: 16px 0; }
.payroll-history__section > header { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: 8px; margin-bottom: 8px; }
.payroll-history h2 { margin: 0; font-size: 17px; }
.payroll-history th { white-space: nowrap; }
.payroll-history summary { cursor: pointer; }
.payroll-history__source { margin-top: 8px; }
.payroll-history dl { display: grid; grid-template-columns: auto 1fr; column-gap: 12px; }
.payroll-history dd { margin: 0; white-space: pre-wrap; }
</style>
